import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { LoggedInUserData } from '@/types/Auth'

export const isAuthenticated = (): boolean => {
    const appStore = useAppStore()
    return appStore.isLoggedIn()
}

let sessionPromise: Promise<boolean> | null = null

// The plane sets an HttpOnly JWT cookie; the only way to know whether we are signed in is
// to ask for the current user. The result is cached for the lifetime of the page.
export const ensureSession = async (): Promise<boolean> => {
    const appStore = useAppStore()
    if (appStore.isLoggedIn()) return true

    if (!sessionPromise) {
        sessionPromise = (async () => {
            const fetchStore = useFetchStore()
            const { statusCode, data } = await fetchStore.fetch('/auth/me').get().json<LoggedInUserData>()

            if (statusCode.value === 200 && data.value) {
                appStore.setLogin(data.value)
                return true
            }

            return false
        })()
    }

    const result = await sessionPromise
    if (!result) sessionPromise = null

    return result
}

export const resetSession = () => {
    sessionPromise = null
}

// Read the session again and write it back over the cached one.
//
// `allowed_instance_types` and `claude_accounts` are the two parts of `/auth/me` that go out
// of date while the tab stays open: an admin granting a second instance type (to themselves,
// on the admin screen) or a workspace of theirs starting elsewhere changes what a start must
// ask and what the caps say, and nothing else would ever re-read it. A start is a keystroke
// away from a container boot, so one extra GET in front of it costs nothing and is what
// keeps the picker from being decided on a stale answer.
export const refreshSession = async (): Promise<void> => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const { statusCode, data } = await fetchStore.fetch('/auth/me').get().json<LoggedInUserData>()

    // A refresh that fails changes nothing: the start goes ahead on what the store already
    // holds, exactly as it would have without this call.
    if (statusCode.value === 200 && data.value) appStore.setLogin(data.value)
}

// Full browser navigation — the plane answers with a 302 to GitLab's OAuth page.
export const gitlabLoginUrl = (redirect: string): string =>
    `/api/startbox/v1/auth/login?redirect=${encodeURIComponent(redirect || '/')}`
